import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

export function useClaimItems(walletAddress) {
  const [claimItems, setClaimItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasTradedToday, setHasTradedToday] = useState(false);
  const [claimedToday, setClaimedToday] = useState({}); // { claimItemId: true/false }

  // 오늘 클레임 여부 확인 (UTC+0 기준)
  const checkTodayClaims = useCallback(async () => {
    if (!walletAddress) return {};

    try {
      const now = new Date();
      const startOfToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0));
      const endOfToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59));

      const { data, error } = await supabase
        .from('claim_histories')
        .select('claim_item_id')
        .eq('wallet_address', walletAddress)
        .gte('claimed_at', startOfToday.toISOString())
        .lte('claimed_at', endOfToday.toISOString());

      if (error) throw error;

      // { claimItemId: true } 형태로 변환
      const claimedMap = {};
      (data || []).forEach(claim => {
        claimedMap[claim.claim_item_id] = true;
      });

      return claimedMap;
    } catch (err) {
      console.error('Error checking today claims:', err);
      return {};
    }
  }, [walletAddress]);

  // 오늘 거래 여부 확인 (UTC+0 기준)
  const checkDailyTrade = useCallback(async () => {
    if (!walletAddress) return false;

    try {
      const now = new Date();
      const startOfToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0));
      const endOfToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59));

      // 지갑 주소 대소문자 무시를 위해 ilike 사용
      const { data, error } = await supabase
        .from('affiliate_histories')
        .select('affiliate_history_id, trade_at, buyer_wallet_address')
        .ilike('buyer_wallet_address', walletAddress)
        .gte('trade_at', startOfToday.toISOString())
        .lte('trade_at', endOfToday.toISOString())
        .limit(1);

      if (error) throw error;

      return data && data.length > 0;
    } catch (err) {
      console.error('Error checking daily trade:', err);
      return false;
    }
  }, [walletAddress]);

  // 월별 띠 쌀숭이 매핑
  const monthlyZodiacData = {
    1: { name: 'Sanho', image: 'Sanho.png' },
    2: { name: 'Nari', image: 'Nari.png' },
    3: { name: 'Bari', image: 'Bari.png' },
    4: { name: 'Rose Glodeer', image: 'Rose%20Glodeer.png' },
    5: { name: 'Mint Glodeer', image: 'Mint%20Glodeer.png' },
    6: { name: 'Iris Glodeer', image: 'Iris%20Glodeer.png' },
    7: { name: 'Waddle', image: 'Waddle.png' },
    8: { name: 'Puff', image: 'Puff.png' },
    9: { name: 'Toddle', image: 'Toddle.png' },
    10: { name: 'Amethyst', image: 'Amethyst.png' },
    11: { name: 'Cosmo Rabbit', image: 'Cosmo%20Rabbit.png' },
    12: { name: 'Serena', image: 'Serena.png' },
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];

  // 클레임 아이템 목록 가져오기
  const fetchClaimItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 현재 월 (UTC 기준, 1-12)
      const currentMonth = new Date().getUTCMonth() + 1;

      // 클레임 아이템 조회
      const { data: items, error: itemsError } = await supabase
        .from('claim_items')
        .select('*')
        .eq('is_active', true)
        .order('required_dust', { ascending: true });

      if (itemsError) throw itemsError;

      // 각 아이템의 확률표 조회 및 월별 동적 변환
      const itemsWithProbabilities = await Promise.all(
        (items || []).map(async (item) => {
          const { data: probs } = await supabase
            .from('claim_probabilities')
            .select('pet_index, probability')
            .eq('claim_item_id', item.id)
            .order('probability', { ascending: false });

          // use_monthly_zodiac가 true면 현재 월에 맞게 이미지 변환 (타이틀은 DB 값 유지)
          let displayItem = { ...item };
          if (item.use_monthly_zodiac) {
            const zodiac = monthlyZodiacData[currentMonth];
            displayItem.item_image_url = `https://cqdypzoxossuzspcoqor.supabase.co/storage/v1/object/public/pet-images/${zodiac.image}`;
          }

          return {
            ...displayItem,
            probabilities: probs || [],
          };
        })
      );

      setClaimItems(itemsWithProbabilities);

      // 오늘 거래 여부 확인
      const traded = await checkDailyTrade();
      setHasTradedToday(traded);

      // 오늘 클레임 여부 확인
      const claimed = await checkTodayClaims();
      setClaimedToday(claimed);
    } catch (err) {
      console.error('Error fetching claim items:', err);
      setError(err.message);
      setClaimItems([]);
    } finally {
      setLoading(false);
    }
  }, [walletAddress, checkDailyTrade, checkTodayClaims]);

  // 클레임 실행 (엣지 펑션 호출)
  const claimReward = useCallback(async (claimItemId, dustPoints) => {
    if (!walletAddress) {
      throw new Error('Wallet address is required');
    }

    try {
      // 현재 로그인된 사용자 세션 가져오기
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('Authentication required');
      }

      // 엣지 펑션 호출
      const { data, error } = await supabase.functions.invoke('claim-reward', {
        body: { claimItemId },
      });

      // 에러 처리 (엣지 펑션이 반환한 에러 메시지 사용)
      if (error) {
        // FunctionsHttpError나 FunctionsRelayError인 경우
        const errorMessage = data?.error || error.message || 'Failed to claim reward';
        throw new Error(errorMessage);
      }

      // 클레임 성공 후 오늘 클레임 상태 업데이트
      setClaimedToday(prev => ({
        ...prev,
        [claimItemId]: true,
      }));

      return {
        success: true,
        petIndex: data.petIndex,
        newDustPoints: data.newDustPoints,
      };
    } catch (err) {
      console.error('Claim error:', err);
      throw err;
    }
  }, [walletAddress]);

  useEffect(() => {
    if (walletAddress) {
      fetchClaimItems();
    } else {
      setClaimItems([]);
      setLoading(false);
    }
  }, [walletAddress, fetchClaimItems]);

  return {
    claimItems,
    loading,
    error,
    hasTradedToday,
    claimedToday,
    claimReward,
    refresh: fetchClaimItems,
  };
}
